import type { AdminCatalogChange } from '@steado/admin-contract/catalog-history';
import type { AdminFoodRequestDetail } from '@steado/admin-contract/foods';

import { formatValue, rowsOf, text } from '@/screens/catalog-history/history-model';

import { plural } from './request-copy';

/**
 * An answer's History batch as the answered request shows it: grouped by the
 * row it changed. One flat list put the AI estimate's retirement ("Active Yes
 * → No") under the new food's name, so it read as if the new food had been
 * switched off. The food the request was answered with comes first, under its
 * name; the estimate it retired comes under a label saying so; then any other
 * food the batch touched (a recipe that named the estimate). Pure, so the
 * grouping is unit-testable.
 */

/** One line: a field and its value, with what it was when the answer changed it. */
export interface AnswerRow {
  readonly key: string;
  readonly field: string;
  /** Null for a value a food was added with: there was nothing before it. */
  readonly before: string | null;
  readonly after: string;
}

export interface AnswerGroup {
  readonly key: string;
  readonly title: string;
  readonly rows: readonly AnswerRow[];
}

export interface AnswerContext {
  /** The food the request was answered with. */
  readonly food: { readonly slug: string | null; readonly name: string };
  /** The AI estimate the answer retired, if it retired one. */
  readonly estimate: { readonly id: string; readonly slug: string } | null;
  readonly moves: AdminFoodRequestDetail['estimateMoves'];
  /** The answer's History batch, so moves from another approval of the request are not counted. */
  readonly batchId: string | null;
  readonly reverted: boolean;
  /** Names of other foods the batch touched, by slug. */
  readonly names: ReadonlyMap<string, string>;
}

/** The AI estimate a new food request was logged as, when its answer retired it into another food. */
export function retiredEstimate(request: AdminFoodRequestDetail): AnswerContext['estimate'] {
  const food = request.food;
  if (request.kind !== 'new_food' || food?.source !== 'ai_estimated') return null;
  return food.id === request.resolvedFoodId ? null : { id: food.id, slug: food.slug };
}

export function answerGroups(changes: readonly AdminCatalogChange[], context: AnswerContext): AnswerGroup[] {
  const bySlug = new Map<string, AdminCatalogChange[]>();
  for (const change of changes) {
    const key =
      context.estimate !== null &&
      (change.rowId === context.estimate.id || change.slug === context.estimate.slug)
        ? context.estimate.slug
        : (change.slug ?? change.rowId);
    bySlug.set(key, [...(bySlug.get(key) ?? []), change]);
  }

  const groups: AnswerGroup[] = [];
  const own = context.food.slug === null ? undefined : bySlug.get(context.food.slug);
  if (own !== undefined && context.food.slug !== null) {
    groups.push({ key: context.food.slug, title: context.food.name, rows: foodRows(own) });
  }
  const retired = context.estimate === null ? undefined : bySlug.get(context.estimate.slug);
  if (retired !== undefined && context.estimate !== null) {
    groups.push({ key: context.estimate.slug, title: estimateTitle(context), rows: foodRows(retired) });
  }
  for (const [slug, list] of bySlug) {
    if (slug === context.food.slug || slug === context.estimate?.slug) continue;
    const named = list.find((change) => change.tableName === 'foods');
    groups.push({
      key: slug,
      title: context.names.get(slug) ?? text(named?.record?.display_name ?? named?.record?.name, slug),
      rows: foodRows(list),
    });
  }
  return groups;
}

/** "The AI estimate: retired, its 2 logs moved to Thekua. The revert moved them back." */
function estimateTitle(context: AnswerContext): string {
  const moves = context.moves !== null && context.moves.batchId === context.batchId ? context.moves : null;
  if (moves === null || moves.meals === 0) return 'The AI estimate: retired';
  const moved = `The AI estimate: retired, its ${plural(moves.meals, 'log')} moved to ${context.food.name}`;
  if (!context.reverted || moves.mealsMovedBack === 0) return moved;
  const back =
    moves.mealsMovedBack === moves.meals
      ? moves.meals === 1
        ? 'it'
        : 'them'
      : `${String(moves.mealsMovedBack)} of them`;
  return `${moved}. The revert moved ${back} back`;
}

/**
 * A food's lines. One the batch added reads as the values it was added with:
 * its figures per 100 g, then each field the batch set on it, latest value
 * only (a dish's diet class is derived from its recipe after the row is
 * written, and "Non vegetarian → Vegetarian" would read as a change to it).
 */
function foodRows(changes: readonly AdminCatalogChange[]): AnswerRow[] {
  const rows = rowsOf(changes, 'food');
  const added = changes.find((change) => change.tableName === 'foods' && change.op === 'insert');
  if (added === undefined) {
    return rows.map((row) => ({ key: row.key, field: row.field, before: row.before, after: row.after }));
  }

  // The row as the batch left it: its insert, then each later write to it, oldest first.
  const record = changes
    .filter((change) => change.tableName === 'foods' && change.rowId === added.rowId)
    .sort((a, b) => (BigInt(a.id) < BigInt(b.id) ? -1 : 1))
    .reduce<Record<string, unknown>>((values, change) => ({ ...values, ...change.record }), {});
  const latest = new Map<string, AnswerRow>();
  for (const row of rows) {
    if (row.isCreation || row.isNutrition) continue;
    latest.delete(row.field);
    latest.set(row.field, { key: row.key, field: row.field, before: null, after: row.after });
  }
  return [
    {
      key: `${added.id}:per100g`,
      field: 'Per 100 g',
      before: null,
      after: [
        formatValue(record.kcal_per_100g, 'kcal'),
        `protein ${formatValue(record.protein_g_per_100g, 'grams')}`,
        `carbohydrate ${formatValue(record.carbs_g_per_100g, 'grams')}`,
        `fat ${formatValue(record.fat_g_per_100g, 'grams')}`,
        `fibre ${formatValue(record.fibre_g_per_100g, 'grams')}`,
      ].join(', '),
    },
    ...latest.values(),
  ];
}
